import { cn } from '@workspace/ui/lib/utils'

export interface Person {
  name: string | null
  kind: 'investor' | 'warm_intro'
  /** The fund they belong to, shown beside the name when we know it. */
  fund?: string | null
  /** Our one-line read of what they said. Never the raw message body. */
  quote?: string | null
}

/**
 * Initials for the avatar, or a dash when the contact has no name on file —
 * common enough to matter, since the roster is scraped. Exported to be tested
 * directly: it is the only branching logic here, and the app has no DOM test
 * setup (rendering is covered by the browser state sweep instead).
 */
export const initials = (name: string | null): string => {
  // Trim first: a leading space makes split() emit an empty first element, so
  // " Elena Vidal" would otherwise initial as "E".
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Who replied, or who the call is with.
 *
 * Named people rather than a count, because that is the whole content of these
 * moments: "two replies" is a notification, "Elena Vidal replied" is a reason to
 * open the page. Message bodies deliberately stay on the replies page — this
 * card is a pointer, not an inbox.
 */
export function PeopleVisual({
  people,
  more,
  footnote,
}: {
  people: Person[]
  /** How many further replies are not shown. */
  more?: number
  footnote?: string
}) {
  return (
    <div className="rounded-xl border border-border/40 bg-card/40 px-5 py-4">
      <div className="flex flex-col gap-2.5">
        {people.map((person, index) => (
          <div
            // Names repeat across tracks and can be null, so position is the
            // only stable identity here.
            key={`${person.name ?? 'unknown'}-${index}`}
            className="flex items-center gap-3"
          >
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
                person.kind === 'warm_intro'
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-primary/15 text-primary'
              )}
            >
              {initials(person.name)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[14px] font-semibold text-foreground">
                {person.name ?? 'A contact'}
                {person.fund && (
                  <span className="font-normal text-muted-foreground"> · {person.fund}</span>
                )}
              </div>
              {person.quote ? (
                <div className="text-[12.5px] text-foreground/75 italic leading-relaxed">
                  {person.quote}
                </div>
              ) : (
                <div className="text-[12px] text-muted-foreground">
                  {person.kind === 'warm_intro'
                    ? 'Founder who can introduce you'
                    : 'Investor you reached out to'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!!more && more > 0 && (
        <p className="mt-3 text-[12.5px] text-muted-foreground">
          and {more} more waiting on the replies page.
        </p>
      )}
      {footnote && <p className="mt-3 text-[12.5px] text-muted-foreground">{footnote}</p>}
    </div>
  )
}
